import { FC, Fragment, useCallback } from "react";
import _ from "lodash";
import Page from "../common/navigation/Page";
import FormFields from "../common/form/FieldsList";
import FormButtons from "../common/form/Buttons";
import BackButton from "../common/navigation/BackButton";
import ContentTitle from "../common/F7/ContentTitle";
import validationUtils, { ValidationError, ValidatedForm } from "../../utils/formValidation";
import { useNavigate } from "react-router-dom";
import { useMap } from "../../App";
import { Field } from "../common/form/FieldHandler";
import Spinner from "../common/F7/Spinner";
import { useCurrentProjectConfig } from "../../hooks/database/useCurrentProjectConfig";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { useCurrentObject } from "../../hooks/database/useCurrentObject";

const ObjectPreviewPage: FC = () => {
  const navigate = useNavigate();

  const { data: inventory } = useCurrentInventory();
  const { data: object } = useCurrentObject();

  const map = useMap()!;

  const prepareFields = useCallback(
    (fieldsWithValues: Record<string, Field>) => {
      if (!object) {
        return {};
      }
      let fkObject2,
        retObject2 = {};
      Object.keys(fieldsWithValues).forEach((key) => {
        if (key.indexOf("_4326") === 0) {
          retObject2 = fieldsWithValues[key];
          fkObject2 = key;
          if (retObject2 && fkObject2) {
            let xy: [number, number];
            if (object.geom.geometry.type === "Point") {
              xy = object.geom.geometry.coordinates;
            } else if (object.geom.geometry.type === "LineString") {
              xy = object.geom.geometry.coordinates[0];
            } else if (object.geom.geometry.type === "Polygon") {
              xy = object.geom.geometry.coordinates[0][0];
            } else {
              throw new Error("Geometry type not implemented");
            }
            fieldsWithValues[fkObject2].value = "EPSG:4326 " + xy[0] + ", " + xy[1];
            if (key === "_4326") {
              fieldsWithValues[fkObject2].value = xy[0] + ", " + xy[1];
            }
            if (key === "_4326l") {
              fieldsWithValues[fkObject2].label += " EPSG:4326";
              fieldsWithValues[fkObject2].value = xy[0] + ", " + xy[1];
            }
            if (key === "_4326v") {
              fieldsWithValues[fkObject2].value = "EPSG:4326 " + xy[0] + ", " + xy[1];
            }
            if (key === "_4326lv") {
              fieldsWithValues[fkObject2].label += " EPSG:4326";
              fieldsWithValues[fkObject2].value = "EPSG:4326 " + xy[0] + ", " + xy[1];
            }
          }
        }
        if (key.indexOf("_currproj") === 0) {
          retObject2 = fieldsWithValues[key];
          fkObject2 = key;
          if (retObject2 && fkObject2) {
            const currProjection = map.getView().getProjection();
            let pair: [number, number];
            if (object.geom.geometry.type === "Point") {
              pair = object.geom.geometry.coordinates;
            } else if (object.geom.geometry.type === "LineString") {
              pair = object.geom.geometry.coordinates[0];
            } else if (object.geom.geometry.type === "Polygon") {
              pair = object.geom.geometry.coordinates[0][0];
            } else {
              throw new Error("Geometry type not implemented");
            }
            const pairs = ol.proj.transform([pair[0], pair[1]], "EPSG:4326", currProjection);
            if (key === "_currproj") {
              fieldsWithValues[fkObject2].value = pairs[0] + ", " + pairs[1];
            }
            if (key === "_currprojl") {
              fieldsWithValues[fkObject2].label += " " + currProjection.getCode();
              fieldsWithValues[fkObject2].value = pairs[0] + ", " + pairs[1];
            }
            if (key === "_currprojv") {
              fieldsWithValues[fkObject2].value = currProjection.getCode() + pairs[0] + ", " + pairs[1];
            }
            if (key === "_currprojlv") {
              fieldsWithValues[fkObject2].label += " " + currProjection.getCode();
              fieldsWithValues[fkObject2].value = currProjection.getCode() + pairs[0] + ", " + pairs[1];
            }
          }
        }
        if (key.indexOf("_3006") === 0) {
          retObject2 = fieldsWithValues[key];
          fkObject2 = key;
          if (retObject2 && fkObject2) {
            const projection = map.getView().getProjection();
            let pair2;
            if (object.geom.geometry.type === "Point") {
              pair2 = object.geom.geometry.coordinates;
            } else if (object.geom.geometry.type === "LineString") {
              pair2 = object.geom.geometry.coordinates[0];
            } else if (object.geom.geometry.type === "Polygon") {
              pair2 = object.geom.geometry.coordinates[0][0];
            } else {
              throw new Error("Geometry type not implemented");
            }
            const pairs2 = ol.proj.transform([pair2[0], pair2[1]], "EPSG:4326", "EPSG:3006");
            if (key === "_3006") {
              fieldsWithValues[fkObject2].value = pairs2[0] + ", " + pairs2[1];
            }
            if (key === "_3006l") {
              fieldsWithValues[fkObject2].label += " " + projection.getCode();
              fieldsWithValues[fkObject2].value = pairs2[0] + ", " + pairs2[1];
            }
            if (key === "_3006v") {
              fieldsWithValues[fkObject2].value = projection.getCode() + pairs2[0] + ", " + pairs2[1];
            }
            if (key === "_3006lv") {
              fieldsWithValues[fkObject2].label += " " + projection.getCode();
              fieldsWithValues[fkObject2].value = projection.getCode() + pairs2[0] + ", " + pairs2[1];
            }
          }
        }
      });
      return fieldsWithValues;
    },
    [map, object]
  );

  const formValues =
    object && inventory
      ? object.data.map((formDoc) => {
          const formDocValues = _.cloneDeep(formDoc.values);
          const form = inventory.forms[formDoc.form]!;
          const fields = form ? _.cloneDeep(form.fields) : {};
          let index = 0;
          let currFormName: string;
          const fieldsWithValues = Object.entries(fields).reduce<Record<string, Field>>((result, [key, value]) => {
            result[key] = { ...value, value: formDocValues[key] ? formDocValues[key].value : null } as any;
            if (index === 0) {
              currFormName = result[key].value ? (result[key].value as string) : "";
            }
            index++;
            return result;
          }, {});
          return {
            templateKey: form.key,
            templateName: form.name,
            formName: currFormName!,
            fields: prepareFields(fieldsWithValues),
          };
        })
      : [];
  const groupedValues = _.groupBy(formValues, "templateName");

  const { data: config } = useCurrentProjectConfig();

  const errorKeys = validationUtils.validateAll({
    forms: formValues,
    rules: config?.config?.formValidation || [],
  });

  const markRequiredFields = useCallback((fields: Record<string, Field>) => {
    const isEmpty = (value: Field["value"]) => {
      return value === "" || typeof value === "undefined";
    };
    for (let field in fields) {
      if (fields[field].required && isEmpty(fields[field].value)) {
        fields[field].error = {
          message: "Obligatoriskt fält",
        };
      }
    }
  }, []);

  const assertCorrectTemplate = useCallback((template: ValidatedForm, errorKeyObjects: ValidationError[]) => {
    let result = false;
    errorKeyObjects.forEach((obj) => {
      // FIXME: what's going on here?
      obj.rule.value1.forEach((v: ValidatedForm) => {
        if (Array.isArray(v)) {
          v = v[0];
        }
        result =
          result ||
          (v.formName && template.formName && v.formName.toLowerCase() === template.formName.toLowerCase()) ||
          (v.templateKey && template.templateKey && v.templateKey.toLowerCase() === template.templateKey.toLowerCase()) ||
          ((v.templateName && template.templateName && v.templateName.toLowerCase() === template.templateName.toLowerCase()) as boolean);
      });
    });
    return result;
  }, []);

  const markErrorFields = useCallback(
    (template: ValidatedForm) => {
      if (errorKeys) {
        let errorKeyObjects = errorKeys.filter((errorObj) => errorObj.hasOwnProperty("key"));
        let error_Keys = errorKeyObjects.map((obj) => obj.key);
        if (!assertCorrectTemplate(template, errorKeyObjects)) {
          return;
        }
        for (let field in template.fields) {
          let foundIx = error_Keys.indexOf(field);
          if (foundIx >= 0) {
            template.fields[field].error = errorKeyObjects[foundIx];
          }
        }
      }
    },
    [assertCorrectTemplate, errorKeys]
  );

  const handleCancel = useCallback(() => navigate("../"), [navigate]);

  if (!object || !inventory) {
    return <Spinner />;
  }

  return (
    <Page noToolbar navbarLeft={<BackButton onClick={handleCancel}>Back</BackButton>} title={object.title}>
      <ContentTitle>
        Preview of all forms in inventory
        <big>
          <strong>{(formValues as any).currFormName}</strong>
        </big>
      </ContentTitle>
      <div>
        {Object.entries(groupedValues).map(([form, values], idx1) => (
          <Fragment key={idx1}>
            <div className="content-block-title">Template name: {form}</div>
            {values.map((val, idx2) => {
              markErrorFields(val);
              markRequiredFields(val.fields);
              return <FormFields key={idx2} fields={val.fields} onChange={() => {}} />;
            })}
          </Fragment>
        ))}
      </div>
      <hr />
      <FormButtons cancelText="Close" onCancel={handleCancel} />
    </Page>
  );
};

export default ObjectPreviewPage;
